import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, Download, ChevronDown, ChevronUp, 
  Phone, Mail, CheckCircle, XCircle, Trash2, Eye,
  LogOut, RefreshCw, Building2, AlertCircle, Users, TrendingUp, DollarSign, X, Bell, BellRing
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [leads, setLeads] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [claimFilter, setClaimFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, claimed_leads: 0 });
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [leadHistory, setLeadHistory] = useState(null);
  const [showLeadHistory, setShowLeadHistory] = useState(false);
  const [expandedLead, setExpandedLead] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        setIsAuthenticated(true);
        toast.success('Welcome to the admin dashboard');
      }
    } catch {
      toast.error('Invalid password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  const fetchDataRef = useRef(null);
  fetchDataRef.current = async () => {
    setIsLoading(true);
    try {
      const [leadsRes, statsRes, contractorsRes, notifsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/leads/stats`),
        axios.get(`${API}/contractors`),
        axios.get(`${API}/notifications`),
      ]);
      setLeads(leadsRes.data);
      setFilteredLeads(leadsRes.data);
      setStats(statsRes.data);
      setContractors(contractorsRes.data);
      setNotifications(notifsRes.data.notifications || []);
      setUnreadCount(notifsRes.data.unread_count || 0);
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDataRef.current();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let result = [...leads];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (lead) =>
          lead.name.toLowerCase().includes(term) ||
          lead.email.toLowerCase().includes(term) ||
          lead.phone.includes(term)
      );
    }

    if (serviceFilter !== 'all') {
      result = result.filter((lead) => lead.service_type === serviceFilter);
    }

    if (statusFilter !== 'all') {
      result = result.filter((lead) => lead.status === statusFilter);
    }

    if (claimFilter === 'claimed') {
      result = result.filter((lead) => lead.claimed_by);
    } else if (claimFilter === 'unclaimed') {
      result = result.filter((lead) => !lead.claimed_by);
    }

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    setFilteredLeads(result);
  }, [leads, searchTerm, serviceFilter, statusFilter, claimFilter, sortField, sortDirection]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus });
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      toast.success('Lead status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getContractorName = (contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    return contractor ? contractor.company_name : 'Unknown';
  };

  const deleteContractor = async (contractorId, companyName) => {
    if (!window.confirm(`Are you sure you want to remove "${companyName}"? Their claimed leads will be released back to the pool.`)) return;
    try {
      await axios.delete(`${API}/contractors/${contractorId}`);
      setContractors(contractors.filter(c => c.id !== contractorId));
      toast.success(`${companyName} has been removed`);
      fetchDataRef.current();
    } catch {
      toast.error('Failed to remove contractor');
    }
  };

  const viewLeadHistory = async (contractor) => {
    setSelectedContractor(contractor);
    try {
      const res = await axios.get(`${API}/contractors/${contractor.id}/lead-history`);
      setLeadHistory(res.data);
      setShowLeadHistory(true);
    } catch {
      toast.error('Failed to load lead history');
    }
  };

  const toggleContractorStatus = async (contractorId, currentStatus) => {
    try {
      await axios.patch(`${API}/contractors/${contractorId}/status`, { is_active: !currentStatus });
      setContractors(contractors.map(c => c.id === contractorId ? { ...c, is_active: !currentStatus } : c));
      toast.success(`Contractor ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const markNotificationRead = async (id) => {
    await axios.patch(`${API}/notifications/${id}/read`);
    setNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.patch(`${API}/notifications/read-all`);
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Service', 'City', 'Lead Tag', 'Message', 'Status', 'Claimed By', 'Lead Cost', 'Date', 'Claimed At'];
    const csvData = filteredLeads.map((lead) => [
      lead.name,
      lead.email,
      lead.phone,
      lead.service_type,
      lead.city || '',
      lead.lead_tag || '',
      (lead.message || '').replace(/,/g, ' '),
      lead.status,
      lead.claimed_by ? getContractorName(lead.claimed_by) : 'Unclaimed',
      lead.lead_cost || 0,
      new Date(lead.created_at).toLocaleDateString(),
      lead.claimed_at ? new Date(lead.claimed_at).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hvac-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredLeads.length} leads`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (service) => {
    const labels = {
      'ac-repair': 'AC Repair',
      'ac-installation': 'AC Installation',
      'heating-repair': 'Heating Repair',
      'heating-installation': 'Heating Installation',
      'duct-cleaning': 'Duct Cleaning',
      'maintenance': 'Maintenance',
      'emergency': 'Emergency'
    };
    return labels[service] || service;
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-green-100 text-green-800 border-green-200',
      contacted: 'bg-blue-100 text-blue-800 border-blue-200',
      sold: 'bg-purple-100 text-purple-800 border-purple-200',
      closed: 'bg-slate-100 text-slate-800 border-slate-200',
      'follow-up': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return styles[status] || styles.new;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0F172A] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">M</span>
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              NOLA HVAC Connect
            </h1>
            <p className="text-slate-500 mt-2">Admin Dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                data-testid="admin-password-input"
              />
            </div>
            <Button type="submit" data-testid="admin-login-btn" className="w-full bg-[#0F172A] hover:bg-slate-800 h-12">
              Access Dashboard
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const unclaimedLeads = leads.filter(l => !l.claimed_by);
  const needsFollowUp = leads.filter(l => l.claimed_by && l.status === 'contacted');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0F172A] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                NOLA HVAC Connect
              </h1>
              <p className="text-xs text-slate-500">Lead Management Dashboard</p>
            </div>
          </a>
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                data-testid="notification-bell"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative"
              >
                {unreadCount > 0 ? <BellRing className="w-4 h-4 text-[#F97316]" /> : <Bell className="w-4 h-4" />}
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-12 w-[400px] bg-white rounded-xl border border-slate-200 shadow-xl z-50 max-h-[500px] overflow-hidden" data-testid="notification-panel">
                  <div className="flex items-center justify-between p-4 border-b border-slate-100">
                    <h3 className="font-bold text-[#0F172A]">Lead Notifications</h3>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-[#0EA5E9] hover:underline">Mark all read</button>
                      )}
                      <button onClick={() => setShowNotifications(false)}>
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[400px]">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-slate-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          data-testid={`notification-${notif.id}`}
                          onClick={() => !notif.read && markNotificationRead(notif.id)}
                          className={`p-4 border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${!notif.read ? 'bg-orange-50/50 border-l-2 border-l-[#F97316]' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-[#0F172A]">{notif.lead_name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {notif.service_type} {notif.city ? `• ${notif.city}` : ''}
                              </p>
                              <div className="flex items-center gap-3 mt-1">
                                <a href={`tel:${notif.lead_phone}`} onClick={e => e.stopPropagation()} className="text-xs text-[#0EA5E9] hover:underline">{notif.lead_phone}</a>
                                <a href={`mailto:${notif.lead_email}`} onClick={e => e.stopPropagation()} className="text-xs text-[#0EA5E9] hover:underline">{notif.lead_email}</a>
                              </div>
                              {notif.message && <p className="text-xs text-slate-400 mt-1 italic truncate">"{notif.message}"</p>}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-slate-400">{new Date(notif.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                              {notif.email_sent ? (
                                <span className="text-xs text-green-600">Email sent</span>
                              ) : (
                                <span className="text-xs text-red-500">Email failed</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Total Leads</p>
            <p className="text-3xl font-bold text-[#0F172A]">{stats.total_leads}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Unclaimed</p>
            <p className="text-3xl font-bold text-red-600">{unclaimedLeads.length}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Claimed</p>
            <p className="text-3xl font-bold text-green-600">{stats.claimed_leads}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Needs Follow-up</p>
            <p className="text-3xl font-bold text-yellow-600">{needsFollowUp.length}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Contractors</p>
            <p className="text-3xl font-bold text-[#0EA5E9]">{contractors.length}</p>
          </div>
        </div>

        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList>
            <TabsTrigger value="leads">All Leads</TabsTrigger>
            <TabsTrigger value="unclaimed" className="relative">
              Unclaimed
              {unclaimedLeads.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unclaimedLeads.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="followup">Follow-up Needed</TabsTrigger>
            <TabsTrigger value="contractors">Contractors</TabsTrigger>
          </TabsList>

          {/* All Leads Tab */}
          <TabsContent value="leads">
            {/* Filters */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-col md:flex-row gap-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, email, or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                  
                  <Select value={claimFilter} onValueChange={setClaimFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Claim Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="claimed">Claimed</SelectItem>
                      <SelectItem value="unclaimed">Unclaimed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="follow-up">Follow-up</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => fetchDataRef.current()} disabled={isLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button onClick={exportToCSV} className="bg-[#0F172A] hover:bg-slate-800">
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">
                        Name
                        {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </div>
                    </TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Claimed By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Lead Cost</TableHead>
                    <TableHead className="cursor-pointer hover:bg-slate-100" onClick={() => handleSort('created_at')}>
                      <div className="flex items-center gap-1">
                        Date
                        {sortField === 'created_at' && (sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                      </div>
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12 text-slate-500">
                        {isLoading ? 'Loading leads...' : 'No leads found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeads.map((lead) => (
                      <React.Fragment key={lead.id}>
                        <TableRow
                          data-testid={`lead-row-${lead.id}`}
                          className={`cursor-pointer hover:bg-slate-50 ${expandedLead === lead.id ? 'bg-sky-50' : ''}`}
                          onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
                        >
                          <TableCell className="w-8">
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedLead === lead.id ? 'rotate-180' : ''}`} />
                          </TableCell>
                          <TableCell className="font-medium">{lead.name}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <a href={`mailto:${lead.email}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-sm text-slate-600 hover:text-[#0EA5E9]">
                                <Mail className="w-3 h-3" />
                                {lead.email}
                              </a>
                              <a href={`tel:${lead.phone}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-sm text-slate-600 hover:text-[#0EA5E9]">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </a>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-slate-50">
                              {getServiceLabel(lead.service_type)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">{lead.city || '—'}</span>
                          </TableCell>
                          <TableCell>
                          {lead.claimed_by ? (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-700">
                                {getContractorName(lead.claimed_by)}
                              </span>
                            </div>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                              Unclaimed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(lead.status)}>
                            {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-[#F97316]">${(lead.lead_cost || 0).toFixed(0)}</span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(lead.created_at)}
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Select value={lead.status} onValueChange={(value) => updateLeadStatus(lead.id, value)}>
                            <SelectTrigger className="w-[120px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="follow-up">Follow-up</SelectItem>
                              <SelectItem value="sold">Sold</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Detail Row */}
                      {expandedLead === lead.id && (
                        <TableRow className="bg-sky-50/50">
                          <TableCell colSpan={10} className="p-0">
                            <div className="px-6 py-4 border-t border-sky-100">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Lead ID</p>
                                  <p className="text-sm text-slate-700 font-mono mt-1">{lead.id.slice(0, 12)}...</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Lead Tag</p>
                                  <p className="text-sm text-slate-700 mt-1">{lead.lead_tag || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">City</p>
                                  <p className="text-sm text-slate-700 mt-1">{lead.city || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Lead Cost</p>
                                  <p className="text-sm font-bold text-[#F97316] mt-1">${(lead.lead_cost || 0).toFixed(2)}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Full Name</p>
                                  <p className="text-sm text-slate-700 mt-1">{lead.name}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Email</p>
                                  <a href={`mailto:${lead.email}`} className="text-sm text-[#0EA5E9] hover:underline mt-1 block">{lead.email}</a>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Phone</p>
                                  <a href={`tel:${lead.phone}`} className="text-sm text-[#0EA5E9] hover:underline mt-1 block">{lead.phone}</a>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Submitted</p>
                                  <p className="text-sm text-slate-700 mt-1">{formatDate(lead.created_at)}</p>
                                </div>
                              </div>
                              {lead.claimed_by && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                  <div>
                                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Claimed By</p>
                                    <p className="text-sm font-medium text-green-700 mt-1">{getContractorName(lead.claimed_by)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Claimed At</p>
                                    <p className="text-sm text-slate-700 mt-1">{lead.claimed_at ? formatDate(lead.claimed_at) : '—'}</p>
                                  </div>
                                </div>
                              )}
                              {lead.message && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Message</p>
                                  <p className="text-sm text-slate-700 bg-white p-3 rounded-lg border border-slate-200 italic">"{lead.message}"</p>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Unclaimed Leads Tab */}
          <TabsContent value="unclaimed">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <h2 className="text-lg font-bold text-[#0F172A]">Unclaimed Leads</h2>
                <p className="text-slate-500 text-sm">— These leads haven't been picked up by any contractor yet</p>
              </div>
              
              {unclaimedLeads.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p>All leads have been claimed!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {unclaimedLeads.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                      <div>
                        <p className="font-medium text-[#0F172A]">{lead.name}</p>
                        <p className="text-sm text-slate-500">{lead.phone} • {lead.email}</p>
                        <Badge variant="outline" className="mt-2">{getServiceLabel(lead.service_type)}</Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">{formatDate(lead.created_at)}</p>
                        <p className="text-xs text-red-600 mt-1">Waiting for contractor</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Follow-up Tab */}
          <TabsContent value="followup">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Phone className="w-6 h-6 text-yellow-500" />
                <h2 className="text-lg font-bold text-[#0F172A]">Customer Follow-up</h2>
                <p className="text-slate-500 text-sm">— Check customer satisfaction after contractor contact</p>
              </div>
              
              {needsFollowUp.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p>No follow-ups needed right now</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {needsFollowUp.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div>
                        <p className="font-medium text-[#0F172A]">{lead.name}</p>
                        <p className="text-sm text-slate-500">{lead.phone} • {lead.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{getServiceLabel(lead.service_type)}</Badge>
                          <span className="text-xs text-slate-500">→</span>
                          <span className="text-sm font-medium text-green-700">{getContractorName(lead.claimed_by)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Select value={lead.status} onValueChange={(value) => updateLeadStatus(lead.id, value)}>
                          <SelectTrigger className="w-[140px] h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="follow-up">Need Follow-up</SelectItem>
                            <SelectItem value="sold">Job Completed</SelectItem>
                            <SelectItem value="closed">No Sale</SelectItem>
                          </SelectContent>
                        </Select>
                        <a href={`tel:${lead.phone}`} className="text-xs text-[#0EA5E9] hover:underline">
                          Call to check satisfaction
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Contractors Tab */}
          <TabsContent value="contractors">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#0EA5E9]" />
                  <h2 className="text-lg font-bold text-[#0F172A]">Registered Contractors</h2>
                  <Badge variant="outline">{contractors.length} total</Badge>
                </div>
              </div>
              
              {contractors.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p>No contractors registered yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contractors.map((contractor) => (
                    <div key={contractor.id} data-testid={`contractor-card-${contractor.id}`} className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="font-bold text-[#0F172A] text-lg">{contractor.company_name}</p>
                            <Badge className={contractor.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {contractor.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{contractor.contact_name}</p>
                          <div className="flex items-center gap-4 mt-1">
                            <a href={`mailto:${contractor.email}`} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0EA5E9]">
                              <Mail className="w-3 h-3" /> {contractor.email}
                            </a>
                            <a href={`tel:${contractor.phone}`} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0EA5E9]">
                              <Phone className="w-3 h-3" /> {contractor.phone}
                            </a>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-3">
                            {contractor.service_areas?.map((area) => (
                              <Badge key={area} variant="outline" className="text-xs">{area}</Badge>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2 min-w-[180px]">
                          <div className="flex items-center gap-4 text-center">
                            <div>
                              <p className="text-2xl font-bold text-[#0EA5E9]">{contractor.leads_claimed || 0}</p>
                              <p className="text-xs text-slate-500">Leads Claimed</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-[#F97316]">${(contractor.balance || 0).toFixed(0)}</p>
                              <p className="text-xs text-slate-500">Balance</p>
                            </div>
                          </div>

                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`view-leads-${contractor.id}`}
                              onClick={() => viewLeadHistory(contractor)}
                              className="text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" /> Lead Flow
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`toggle-status-${contractor.id}`}
                              onClick={() => toggleContractorStatus(contractor.id, contractor.is_active)}
                              className={`text-xs ${contractor.is_active ? 'text-yellow-600 hover:text-yellow-700' : 'text-green-600 hover:text-green-700'}`}
                            >
                              {contractor.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`delete-contractor-${contractor.id}`}
                              onClick={() => deleteContractor(contractor.id, contractor.company_name)}
                              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Lead History Dialog */}
        <Dialog open={showLeadHistory} onOpenChange={setShowLeadHistory}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0EA5E9]" />
                {selectedContractor?.company_name} — Lead Flow
              </DialogTitle>
            </DialogHeader>
            {leadHistory && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-[#0EA5E9]">{leadHistory.total_claimed}</p>
                    <p className="text-xs text-slate-500">Total Claimed</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-[#F97316]">${leadHistory.total_spent.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">Total Spent</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-green-600">${(selectedContractor?.balance || 0).toFixed(2)}</p>
                    <p className="text-xs text-slate-500">Current Balance</p>
                  </div>
                </div>

                {leadHistory.claimed_leads.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No leads claimed yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-500">Claimed Leads (most recent first)</p>
                    {leadHistory.claimed_leads.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div>
                          <p className="font-medium text-sm text-[#0F172A]">{lead.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{getServiceLabel(lead.service_type)}</Badge>
                            <span className="text-xs text-slate-400">{lead.phone}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-[#F97316]">${(lead.lead_cost || 0).toFixed(2)}</p>
                          <p className="text-xs text-slate-400">{lead.claimed_at ? new Date(lead.claimed_at).toLocaleDateString() : '—'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboard;
